import {
  CONFIDENCE_LEVELS,
  COVERAGE_LABELS,
  ENGAGEMENT_STATUSES,
  FINDINGS,
  MAPPING_STATES,
  PRIORITIES,
  SEVERITIES,
  STAGES,
} from '@fruuit/shared/vocab'
import { Badge, Card, PageHeader, StatusBadge, statusLabel } from '@fruuit/ui'
import type { ReactNode } from 'react'
import { coverageLabelTone } from '../../features/coverage/format'
import { useTitle } from '../../lib/useTitle'

/**
 * S11 Methodology (GAP-UI-025, STAT-012): original copy defining every status,
 * stage, coverage label and confidence level the portal shows. The term lists
 * come from the shared vocabularies, so a new value can't go undefined unnoticed.
 */

const FINDING_TEXT: Record<string, string> = {
  'No material concern identified':
    'The reviewed evidence did not show a material breach of the international norms we screen against. It does not mean that every possible concern has been excluded; the company stays in the review schedule.',
  'Further review required':
    'Credible signals exist, but the evidence is not yet complete enough for a conclusion. Research continues and the company is watched more closely.',
  'Substantiated norms concern':
    'Reviewed evidence supports a concern about the company’s conduct against an international norm. This is a research conclusion, not an investment recommendation.',
  'Insufficient information':
    'Too little reliable evidence was available to reach any conclusion. This is never treated as a clean result.',
}

const STAGE_TEXT: Record<string, string> = {
  'S0 Baseline': 'The starting position is recorded, before any change is evidenced.',
  'S1 Dialogue established': 'The company engages in substantive dialogue on the objective.',
  'S2 Commitment recorded': 'The company has made a specific, public or written commitment.',
  'S3 Implementation evidenced':
    'Independent evidence shows the commitment being put into practice.',
  'S4 Outcome evidenced': 'Independent evidence shows the intended outcome has been achieved.',
}

const LABEL_TEXT: Record<string, string> = {
  'Within recorded scope':
    'The latest internal source check completed and the next review is not yet due.',
  'Review overdue':
    'The scheduled review date has passed. The last published finding still stands until it is reviewed.',
  'Coverage gap: source check partial':
    'The latest source check did not cover every planned source. A partial check can’t support a clean finding.',
  'Coverage gap: source check failed':
    'The latest source check failed. Coverage is unknown, not clear, until it is rerun.',
  'Source review not completed': 'No completed source check is recorded for this company yet.',
}

const MAPPING_TEXT: Record<string, string> = {
  verified:
    'The holding’s identifier is matched to a company in the Fruuit master by a typed identifier.',
  'needs review':
    'A possible match exists or the identifier is a client code; a Fruuit manager confirms it.',
  unresolved:
    'The identifier is not in the master. The holding stays visible as a mapping exception.',
  'out of scope':
    'The holding is outside corporate coverage, for example a sovereign or municipal bond.',
}

const CONFIDENCE_TEXT: Record<string, string> = {
  high: 'Several independent, reliable sources agree.',
  moderate: 'Reliable evidence with some gaps or a single strong source.',
  low: 'Limited or partly contradictory evidence; the finding may change.',
  insufficient: 'The evidence can’t support a confident judgment either way.',
}

const SEVERITY_TEXT: Record<string, string> = {
  not_identified: 'No material harm was identified in the reviewed evidence.',
  low: 'Limited harm, contained and reversible.',
  moderate: 'Material harm to people or the environment, or a systemic weakness.',
  serious: 'Significant harm, with broad or lasting effects.',
  severe: 'Grave, widespread or irreversible harm.',
  undetermined: 'The evidence does not allow severity to be judged yet.',
}

const PRIORITY_TEXT: Record<string, string> = {
  'Routine monitoring': 'Regular scheduled review.',
  'Watchlist / further review': 'Closer monitoring while evidence develops.',
  'Active research / engagement': 'Research or an engagement case is under way.',
  'Urgent review': 'New information needs prompt review.',
}

const CASE_TEXT: Record<string, string> = {
  active: 'Dialogue and evidence work are progressing.',
  stalled: 'The company has not responded or progress has stopped; escalation may be considered.',
  closed: 'The case is complete, with a recorded reason.',
}

function Term({ term, children }: { term: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:gap-6">
      <dt>{term}</dt>
      <dd className="text-sm leading-relaxed text-ink-soft">{children}</dd>
    </div>
  )
}

export function MethodologyPage() {
  useTitle('Methodology')
  return (
    <>
      <PageHeader
        eyebrow="How this works"
        title="Three steps. One clear boundary."
        description="Signals help us find a question. People judge the evidence. Investors see the reviewed work."
      />
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        {[
          [
            '1',
            'Find the question',
            'Monitored sources and data feeds raise signals about a company.',
            'An unchecked lead',
          ],
          [
            '2',
            'Judge the evidence',
            'An analyst assesses the evidence and an independent reviewer approves it.',
            'A reasoned assessment',
          ],
          [
            '3',
            'Show the work',
            'Only reviewed and released findings reach your portal, with their sources.',
            'A reviewed investor view',
          ],
        ].map(([n, title, text, output]) => (
          <Card key={n} variant="tint">
            <p className="font-display text-2xl font-semibold text-teal">{n}</p>
            <h2 className="mt-1 font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-soft">{text}</p>
            <p className="mt-3 text-xs font-semibold tracking-[0.06em] text-teal-500 uppercase">
              Output: {output}
            </p>
          </Card>
        ))}
      </div>
      <div className="space-y-6">
        <Card title="Research findings" eyebrow="What a finding means">
          <dl className="divide-y divide-line">
            {FINDINGS.values.map((f) => (
              <Term key={f} term={<StatusBadge status={f} label={f} />}>
                {FINDING_TEXT[f]}
              </Term>
            ))}
            <Term term={<StatusBadge status="Not yet reviewed" label="Not yet reviewed" />}>
              The holding maps to a company, but no assessment has been published yet.
            </Term>
          </dl>
        </Card>
        <Card title="Review status" eyebrow="Coverage labels">
          <dl className="divide-y divide-line">
            {COVERAGE_LABELS.values.map((l) => (
              <Term
                key={l}
                term={
                  <Badge tone={coverageLabelTone(l)} dot>
                    {l}
                  </Badge>
                }
              >
                {LABEL_TEXT[l]}
              </Term>
            ))}
          </dl>
        </Card>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card title="Engagement stages" eyebrow="Objective progress">
            <dl className="divide-y divide-line">
              {STAGES.values.map((s) => (
                <Term key={s} term={<StatusBadge status={s} />}>
                  {STAGE_TEXT[s]}
                </Term>
              ))}
            </dl>
            <p className="mt-3 text-xs leading-relaxed text-sage">
              Only published, independently reviewed milestones move a stage. Stages do not measure
              investment performance or attributable real-world impact.
            </p>
          </Card>
          <Card title="Case status" eyebrow="Engagements">
            <dl className="divide-y divide-line">
              {ENGAGEMENT_STATUSES.values.map((s) => (
                <Term
                  key={s}
                  term={<StatusBadge status={s} label={ENGAGEMENT_STATUSES.labels[s]} />}
                >
                  {CASE_TEXT[s]}
                </Term>
              ))}
            </dl>
            <p className="mt-3 text-xs leading-relaxed text-sage">
              Case status and objective progress answer different questions: a company may respond
              while independent evidence remains outstanding.
            </p>
          </Card>
          <Card title="Confidence" eyebrow="How sure we are">
            <dl className="divide-y divide-line">
              {CONFIDENCE_LEVELS.values.map((c) => (
                <Term
                  key={c}
                  term={
                    <span className="text-sm font-semibold">{CONFIDENCE_LEVELS.labels[c]}</span>
                  }
                >
                  {CONFIDENCE_TEXT[c]}
                </Term>
              ))}
            </dl>
          </Card>
          <Card title="Severity" eyebrow="How serious it is">
            <dl className="divide-y divide-line">
              {SEVERITIES.values.map((s) => (
                <Term
                  key={s}
                  term={<span className="text-sm font-semibold">{SEVERITIES.labels[s]}</span>}
                >
                  {SEVERITY_TEXT[s]}
                </Term>
              ))}
            </dl>
          </Card>
          <Card title="Mapping states" eyebrow="Holdings">
            <dl className="divide-y divide-line">
              {MAPPING_STATES.values.map((m) => (
                <Term key={m} term={<StatusBadge status={m} label={statusLabel(m)} />}>
                  {MAPPING_TEXT[m]}
                </Term>
              ))}
            </dl>
          </Card>
          <Card title="Priority" eyebrow="Research attention">
            <dl className="divide-y divide-line">
              {PRIORITIES.values.map((p) => (
                <Term key={p} term={<StatusBadge status={p} label={p} />}>
                  {PRIORITY_TEXT[p]}
                </Term>
              ))}
            </dl>
          </Card>
        </div>
        <Card title="Dates mean different things">
          <dl className="divide-y divide-line">
            <Term term={<span className="text-sm font-semibold">Event date</span>}>
              When something happened, with its precision (day, month or year).
            </Term>
            <Term term={<span className="text-sm font-semibold">Publication date</span>}>
              When a source published it.
            </Term>
            <Term term={<span className="text-sm font-semibold">Retrieved / reviewed</span>}>
              When Fruuit collected and reviewed the source.
            </Term>
            <Term term={<span className="text-sm font-semibold">Evidence reviewed</span>}>
              The effective date of the latest published assessment.
            </Term>
          </dl>
          <p className="mt-3 text-xs leading-relaxed text-sage">
            Limits stay visible: empty or failed source checks can’t support a clean screen, and
            duplicate reports of one event are not separate controversies. No licensed provider
            score or proprietary rating methodology is reproduced here.
          </p>
        </Card>
      </div>
    </>
  )
}
